import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { StandardCase } from "@src/lms/jelly/extensions/layouts/standard-case/StandardCase";
import { useBlocks, useUi } from "@jelly/lesson";
import { Accordion, AccordionItem } from "@src/lms/jelly/extensions/ui";
import { useIntl } from "react-intl";

import { messages } from "./Interviews.messages";
import styles from "./Interviews.module.scss";

import * as appConfig from "appConfig";
import { BioModal1 } from "./components/BioModal1";
import { BioModal2 } from "./components/BioModal2";
import { BioModal3 } from "./components/BioModal3";
import { BioModal4 } from "./components/BioModal4";
import { AccordionContext } from "@src/lms/jelly/extensions/ui/accordion/AccordionContext";

interface Interviews {
  title: string;
}

type InterviewSlide = "julius" | "michael" | "ana" | "daniel";

interface InterviewsContextData {
  setActiveSlide: (slide: InterviewSlide) => void;
  activeSlide: InterviewSlide;
}

const InterviewsContext = React.createContext<InterviewsContextData>({
  activeSlide: "julius",
  setActiveSlide: () => {
    throw new Error("Not implemented!");
  },
});

export const Interviews: React.FC<Interviews> = ({ title }) => {
  const [activeSlide, setActiveSlide] = useState<InterviewSlide>("julius");

  return (
    <InterviewsContext.Provider
      value={{
        setActiveSlide,
        activeSlide,
      }}
    >
      <InterviewsContent title={title} />
    </InterviewsContext.Provider>
  );
};

const InterviewsContent: React.FC<Interviews> = ({ title }) => {
  const ui = useUi();
  const blocks = useBlocks();
  const { formatMessage } = useIntl();
  const { activeSlide } = useContext(InterviewsContext);

  const [bioModal1Open, setBioModal1Open] = useState(false);
  const [bioModal2Open, setBioModal2Open] = useState(false);
  const [bioModal3Open, setBioModal3Open] = useState(false);
  const [bioModal4Open, setBioModal4Open] = useState(false);

  const handleBioModal1Toggle = useCallback(() => setBioModal1Open((open) => !open), []);
  const handleBioModal2Toggle = useCallback(() => setBioModal2Open((open) => !open), []);
  const handleBioModal3Toggle = useCallback(() => setBioModal3Open((open) => !open), []);
  const handleBioModal4Toggle = useCallback(() => setBioModal4Open((open) => !open), []);

  const accordion = (
    <div className={styles.accordion}>
      <ui.Scrollbars>
        <Accordion allowMultiOpen={false} defaultOpen={["julius"]}>
          <AccordionObserver />
          <AccordionItem id="julius" title={formatMessage(messages.accordionItem1Title)} unmountChildrenWhenHidden>
            <div style={{ width: "100%", height: "142px" }}>
              <blocks.image src={`${appConfig.STATIC_ASSETS_URL}/lms/case/case05/interviews/1.jpg`} />
            </div>
            <div className={styles.imageDescription}>{formatMessage(messages.accordionItem1ImageDescription)}</div>
            <p>{formatMessage(messages.accordionItem1Description)}</p>
            <ui.Button shape={"normal"} theme={"primary"} size={"small"} onClick={handleBioModal1Toggle}>
              {formatMessage(messages.bioNote)}
            </ui.Button>
          </AccordionItem>
          <AccordionItem id="michael" title={formatMessage(messages.accordionItem2Title)} unmountChildrenWhenHidden>
            <div style={{ width: "100%", height: "142px" }}>
              <blocks.image src={`${appConfig.STATIC_ASSETS_URL}/lms/case/case05/interviews/2.jpg`} />
            </div>
            <div className={styles.imageDescription}>{formatMessage(messages.accordionItem2ImageDescription)}</div>
            <p>{formatMessage(messages.accordionItem2Description)}</p>
            <ui.Button shape={"normal"} theme={"primary"} size={"small"} onClick={handleBioModal2Toggle}>
              {formatMessage(messages.bioNote)}
            </ui.Button>
          </AccordionItem>
          <AccordionItem id="ana" title={formatMessage(messages.accordionItem3Title)} unmountChildrenWhenHidden>
            <div style={{ width: "100%", height: "142px" }}>
              <blocks.image src={`${appConfig.STATIC_ASSETS_URL}/lms/case/case05/interviews/3.jpg`} />
            </div>
            <div className={styles.imageDescription}>{formatMessage(messages.accordionItem3ImageDescription)}</div>
            <p>{formatMessage(messages.accordionItem3Description)}</p>
            <ui.Button shape={"normal"} theme={"primary"} size={"small"} onClick={handleBioModal3Toggle}>
              {formatMessage(messages.bioNote)}
            </ui.Button>
          </AccordionItem>
          <AccordionItem id="daniel" title={formatMessage(messages.accordionItem4Title)} unmountChildrenWhenHidden>
            <div style={{ width: "100%", height: "142px" }}>
              <blocks.image src={`${appConfig.STATIC_ASSETS_URL}/lms/case/case05/interviews/4.jpg`} />
            </div>
            <div className={styles.imageDescription}>{formatMessage(messages.accordionItem4ImageDescription)}</div>
            <p>{formatMessage(messages.accordionItem4Description)}</p>
            <ui.Button shape={"normal"} theme={"primary"} size={"small"} onClick={handleBioModal4Toggle}>
              {formatMessage(messages.bioNote)}
            </ui.Button>
          </AccordionItem>
        </Accordion>
      </ui.Scrollbars>
    </div>
  );

  // Map active slide to video URL
  let videoSrc: string;
  switch (activeSlide) {
    case "julius":
      videoSrc = "https://youtu.be/LEevMMaDlVk";
      break;
    case "michael":
      videoSrc = "https://youtu.be/3MTN9_cAS74";
      break;
    case "ana":
      videoSrc = "https://youtu.be/ZgAgg_1eebM";
      break;
    case "daniel":
      videoSrc = "https://youtu.be/-fHDqpmczlY";
      break;
  }

  return (
    <StandardCase title={title} leftComponent={accordion}>
      <div className={styles.gallery}>
        <div className={styles.video}>
          <blocks.video src={videoSrc} provider={"youtube"} />
        </div>
      </div>
      <BioModal1 visible={bioModal1Open} onClose={handleBioModal1Toggle} />
      <BioModal2 visible={bioModal2Open} onClose={handleBioModal2Toggle} />
      <BioModal3 visible={bioModal3Open} onClose={handleBioModal3Toggle} />
      <BioModal4 visible={bioModal4Open} onClose={handleBioModal4Toggle} />
    </StandardCase>
  );
};

const AccordionObserver: React.FC = () => {
  const { setActiveSlide } = useContext(InterviewsContext);
  const accordionContext = useContext(AccordionContext);
  const previouslyOpen = useRef<string | null>(null);

  useEffect(() => {
    const openItem = accordionContext.openIds[0] ?? null;
    const userOpenedAnotherItem = previouslyOpen.current != openItem;
    if (userOpenedAnotherItem) {
      previouslyOpen.current = openItem;
      let slide: InterviewSlide = "julius";
      switch (openItem) {
        case "julius":
          slide = "julius";
          break;
        case "michael":
          slide = "michael";
          break;
        case "ana":
          slide = "ana";
          break;
        case "daniel":
          slide = "daniel";
          break;
      }

      setActiveSlide(slide);
    }
  }, [accordionContext.openIds, setActiveSlide]);

  return null;
};
